// ContactPage.styled.js
import styled, { keyframes, css } from 'styled-components';

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

export const ContactPageContainer = styled.div`
    min-height: 100vh;
    padding: 0rem 2rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    background: linear-gradient(-45deg, #0f0f1f, #1a1a2f, #16213e, #0f3460);
    background-size: 400% 400%;
    animation: ${gradientShift} 15s ease infinite;

    &::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: url('/img/LogoDuoaColor.png');
        background-size: 400px;
        background-repeat: repeat;
        background-position: center;
        opacity: 0.03;
        z-index: -1;
    }

    @media (max-width: 768px) {
        padding: 2rem 1rem;
    }
`;

export const SectionTitle = styled.h1`
    font-size: 3.5rem;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #ff6b8b, #6b5bff, #4ecdc4);
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 3s ease infinite;
    font-weight: 800;
    letter-spacing: -0.02em;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }
`;

export const SectionSubtitle = styled.p`
    text-align: center;
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 4rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    animation: ${fadeInUp} 0.8s ease-out;

    @media (max-width: 768px) {
        font-size: 1rem;
        margin-bottom: 2rem;
    }
`;

export const ContactGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr; /* Dividido exactamente en 2 */
    gap: 4rem;
    max-width: 1200px;
    margin: 0 auto;
    align-items: start;
    min-height: 60vh;

    /* Aseguramos que ambas columnas tengan el mismo ancho */
    & > * {
        min-width: 0; /* Previene overflow */
    }

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
        min-height: auto;
    }

    @media (max-width: 768px) {
        gap: 2rem;
    }
`;

export const FormContainer = styled.div`
    animation: ${fadeInUp} 0.8s ease-out 0.2s both;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;

    /* Aseguramos que el formulario ocupe el espacio disponible */
    & > form {
        height: auto;
    }
`;

export const SocialContainer = styled.div`
    animation: ${fadeInUp} 0.8s ease-out 0.4s both;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;

    h3 {
        color: white;
        font-size: 1.5rem;
        margin-bottom: 2rem;
        text-align: center;
        font-weight: 600;
        
        @media (min-width: 969px) {
            text-align: left; /* Alineado a la izquierda en desktop */
        }
    }
`;

export const ContactForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 100%;

    .form-group {
        position: relative;
    }

    input,
    textarea {
        width: 100%;
        padding: 1.2rem 1.5rem;
        background: rgba(255, 255, 255, 0.1);
        border: 2px solid rgba(255, 255, 255, 0.1);
        border-radius: 15px;
        color: white;
        font-size: 1rem;
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        box-sizing: border-box;

        &::placeholder {
            color: rgba(255, 255, 255, 0.5);
        }

        &:focus {
            outline: none;
            border-color: #6b5bff;
            background: rgba(255, 255, 255, 0.15);
            box-shadow: 0 0 30px rgba(107, 91, 255, 0.3);
        }

        &:hover {
            border-color: rgba(255, 255, 255, 0.3);
        }
    }

    textarea {
        resize: vertical;
        min-height: 150px;
        font-family: inherit;
    }

    button {
        background: linear-gradient(135deg, #ff6b8b, #6b5bff);
        color: white;
        padding: 1.2rem 2rem;
        border: none;
        border-radius: 15px;
        font-size: 1.1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        width: 100%;
        max-width: 300px;
        margin: 0 auto;

        &:hover:not(:disabled) {
            transform: translateY(-3px);
            box-shadow: 0 15px 35px rgba(107, 91, 255, 0.4);
            animation: ${pulse} 1s infinite;
        }

        &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }

        &.submitting {
            background: linear-gradient(135deg, #6b5bff, #4ecdc4);
        }

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.5s;
        }

        &:hover::before {
            left: 100%;
        }

        @media (min-width: 969px) {
            margin: 0; /* En desktop quitamos el centrado automático */
            max-width: none;
        }
    }
    input.error,
    textarea.error {
        border-color: #ff6b8b !important;
        box-shadow: 0 0 10px rgba(255, 107, 139, 0.3) !important;
    }

    .error-message {
        color: #ff6b8b;
        font-size: 0.8rem;
        margin-top: 0.5rem;
        display: block;
        font-weight: 500;
    }

    .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .checkbox-label {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.9rem;
        line-height: 1.5;

        input[type="checkbox"] {
            width: 20px;
            height: 20px;
            min-width: 20px;
            margin-top: 2px;
            accent-color: #6b5bff;
            cursor: pointer;
            flex-shrink: 0;
        }

        .checkbox-text {
            flex: 1;
        }

        a {
            color: #6b5bff;
            text-decoration: underline;
            transition: color 0.3s ease;

            &:hover {
                color: #ff6b8b;
            }
        }
    }
`;

export const SocialLinks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 100%;
`;

export const SocialCard = styled.a`
    display: flex;
    align-items: center;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 15px;
    text-decoration: none;
    color: white;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    animation: ${fadeInUp} 0.6s ease-out ${props => props.delay || 0}ms both;

    &:hover {
        transform: translateY(-5px);
        border-color: var(--accent-color);
        background: rgba(255, 255, 255, 0.15);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
        
        .arrow {
            transform: translateX(5px);
            color: var(--accent-color);
        }
    }

    .social-info {
        flex: 1;
        margin-left: 1rem;
        
        .platform {
            display: block;
            font-weight: 600;
            font-size: 1.1rem;
            margin-bottom: 0.25rem;
        }
        
        .description {
            display: block;
            font-size: 0.9rem;
            color: rgba(255, 255, 255, 0.7);
        }
    }

    .arrow {
        font-size: 1.2rem;
        transition: all 0.3s ease;
        color: rgba(255, 255, 255, 0.5);
    }
`;

export const SocialIcon = styled.div`
    font-size: 2rem;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    transition: all 0.3s ease;

    .social-icon-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

export const StatusMessage = styled.div`
    padding: 1rem 1.5rem;
    border-radius: 10px;
    text-align: center;
    font-weight: 500;
    margin-top: 1rem;
    animation: ${fadeInUp} 0.5s ease-out;

    ${props => props.type === 'success' && css`
        background: rgba(76, 175, 80, 0.2);
        border: 1px solid rgba(76, 175, 80, 0.5);
        color: #4CAF50;
    `}

    ${props => props.type === 'error' && css`
        background: rgba(244, 67, 54, 0.2);
        border: 1px solid rgba(244, 67, 54, 0.5);
        color: #F44336;
    `}
`;

